import React, { useState } from 'react';
import { isSameDay, isWeekend } from 'date-fns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PickersDay, PickersDayProps, DatePicker as XDatePicker } from '@mui/x-date-pickers';
import { getLocale, translate } from 'translations/translator';
import CommonTranslationVariant from 'translations/variants/common';
import SpanishDateUtils from 'date-fns/locale/es';
import { Box, Typography, styled } from '@mui/material';

const getTranslation = (key: CommonTranslationVariant) => translate('common', key);

interface CustomPickerDayProps extends PickersDayProps<any> {
  isSelected: boolean;
}

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: prop => prop !== 'isSelected'
})<CustomPickerDayProps>(({ theme, isSelected }) => ({
  borderRadius: '50%',
  ...(isSelected && {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover, &:focus': {
      backgroundColor: `${theme.palette.primary.main} !important`
    }
  })
})) as React.ComponentType<CustomPickerDayProps>;

type DatePickerProps = {
  onChange: (name: string, value: any) => void;
  error?: string | undefined;
  format?: string;
  value: string[];
  SubmitChildComponent?: React.ReactElement;
  // labelFunc: (dates: string[], invaliddDates) => any;
  label?: string;
  sx?: React.CSSProperties;
  shouldDisableWeekends?: boolean;
  shouldDisableDate?: any;
};

export const MultiDayDatePicker: React.FC<DatePickerProps> = ({
  value,
  format = getTranslation(CommonTranslationVariant.uiDateFormat),
  onChange,
  label,
  sx,
  SubmitChildComponent,
  shouldDisableDate = () => false,
  shouldDisableWeekends
}) => {
  const [dates, setDates] = useState<any>(value);

  const handleElementClick = (e, day) => {
    e.stopPropagation();
    const idx = dates.findIndex(date => isSameDay(date, day));
    if (idx !== -1) {
      dates.splice(idx, 1);
    } else {
      dates.push(day);
    }
    setDates(dates.map(date => date));
  };

  const Day = props => {
    const { day, ...rest } = props;
    return (
      <CustomPickersDay
        {...rest}
        day={day}
        sx={{ px: 2.5 }}
        disableMargin
        selected={!!dates.find(date => isSameDay(date, props.day))}
        isSelected={!!dates.find(date => isSameDay(date, props.day))}
        onClick={e => handleElementClick(e, props.day)}
      />
    );
  };

  const disableWeekends = date => isWeekend(date);

  const getDisabledDates = date => {
    if (!shouldDisableDate && !shouldDisableDate) return false;
    if (shouldDisableWeekends) {
      return disableWeekends(date) || shouldDisableDate?.(date);
    }
    return shouldDisableDate?.(date);
  };

  const handleOnChange = () => onChange('selectedDates', dates);

  const handleOnClick = () => {
    SubmitChildComponent?.props.onClick();
    setDates([]);
  };

  const MaybeSubmitButton =
    SubmitChildComponent &&
    React.cloneElement(SubmitChildComponent, {
      onClick: handleOnClick,
      disabled: !dates?.length
    });

  const MaybeSetSpanishLocale = getLocale() === 'es-US' && {
    locale: SpanishDateUtils
  };

  const { width, marginTop, ...restSx } = sx ?? {};

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: width ?? '100%',
          marginTop: marginTop ?? '0',
          borderRadius: '8px'
        }}
      >
        {label && (
          <Typography variant="caption" sx={{ fontSize: '12px', fontWeight: 400 }}>
            {label}
          </Typography>
        )}
        <LocalizationProvider dateAdapter={AdapterDateFns} {...MaybeSetSpanishLocale}>
          <XDatePicker
            closeOnSelect={false}
            onChange={handleOnChange}
            format={format}
            sx={{
              width: '100%',
              fontSize: '12px',
              fontWeight: 400,
              borderRadius: '8px',
              ...restSx
            }}
            shouldDisableDate={getDisabledDates}
            slots={{ day: Day }}
            slotProps={{
              day: () =>
                ({
                  selectedDay: value
                } as any),
              textField: textFieldProps => ({
                ...textFieldProps,
                InputProps: {
                  value: dates?.map(date => new Date(date).toLocaleDateString('en-us')).join(', ')
                }
              })
            }}
          />
        </LocalizationProvider>
      </Box>
      {MaybeSubmitButton}
    </>
  );
};
